import { Checkbox, Form, FormInstance, Input, Row } from "antd";
import { Rule } from "antd/es/form";
import { useCallback, useMemo, useState } from "react";
import { UploadFile } from "./UploadFile";
import { useSave } from "../hooks/useSave";
import { FormButtons } from "../../../../components/DefaultFormButtons";
import { useDeleteFromForm } from "../hooks/useDeleteFromForm";
import { TemplateItem } from "../../../../types/Template";
import { useGetInitialValues } from "../hooks/useGetInitialValues";
import { useTemplateValidationError } from "../hooks/useTemplateValidationError";

type TemplateFormProps = {
  data: TemplateItem | null;
  setData: React.Dispatch<React.SetStateAction<TemplateItem | null>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  parentId: string;
  isParentHasDefault: boolean;
  reloadData: () => void;
};

export const TemplateForm = ({
  data,
  reloadData,
  setIsOpen,
  setData,
  isParentHasDefault,
  parentId,
}: TemplateFormProps) => {
  const [form]: FormInstance[] = Form.useForm();

  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const close = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, [setData, setIsOpen]);

  const { errors, onValidationFail } = useTemplateValidationError();

  const { onSave } = useSave(
    parentId,
    form,
    close,
    reloadData,
    setIsDisabled,
    onValidationFail
  );

  const { isDefault, initialData, id } = useGetInitialValues(data);

  const nameRules: Rule[] = useMemo(
    () => [
      {
        required: true,
        validateTrigger: "onSubmit",
      },
    ],
    []
  );

  const { onDelete } = useDeleteFromForm(id, close, reloadData);

  return (
    <Row className="h-100 d-flex position-relative flex-column">
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="pa-6 activities__container overflow-auto"
        disabled={isDisabled}
        initialValues={initialData}
      >
        <Form.Item name="name" label="Name" rules={nameRules} className="mb-4">
          <Input />
        </Form.Item>

        <UploadFile
          form={form}
          data={data}
          errors={errors}
          onValidationFail={onValidationFail}
          setIsDisabled={setIsDisabled}
        />

        {isParentHasDefault && (
          <Form.Item name="isDefault" valuePropName="checked">
            <Checkbox disabled={isDefault || isDisabled}>
              Set as Default
            </Checkbox>
          </Form.Item>
        )}
      </Form>

      <FormButtons
        currentId={id}
        onSaveClick={onSave}
        onCancelClick={close}
        onDeleteClick={onDelete}
        isFormDisabled={isDisabled}
        controller="ReportTemplates"
      />
    </Row>
  );
};
